import { defineComponent, ref, onMounted, computed, watch } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import Header from '@/components/HeaderDashboard.vue';
import Swal from 'sweetalert2';
import { SearchIcon, UserIcon, ShoppingCartIcon, TrashIcon, RefreshCwIcon, CoinsIcon, ListIcon, FileTextIcon, PointerIcon, LoaderIcon, CheckCircleIcon, LockIcon, XOctagonIcon, AlertCircleIcon, XIcon, PlusCircleIcon } from 'lucide-vue-next';
import { OrdenVentaService } from '@/services/OrdenVentaService';
import { NotaCreditoService } from '@/services/NotaCreditoService';
import { PagoVentaService } from '@/services/PagoVentaService';
export default defineComponent({
    name: 'CajaFacturacion',
    components: {
        Sidebar, Header, SearchIcon, UserIcon, ShoppingCartIcon, TrashIcon, RefreshCwIcon,
        CoinsIcon, ListIcon, FileTextIcon, PointerIcon, LoaderIcon, CheckCircleIcon,
        LockIcon, XOctagonIcon, AlertCircleIcon, XIcon, PlusCircleIcon
    },
    setup() {
        const ordenes = ref([]);
        const loading = ref(true);
        const facturando = ref(false);
        const anulando = ref(false);
        const modoHistorial = ref(false);
        const filtroCodigo = ref('');
        const ordenSeleccionada = ref(null);
        const metodoPago = ref('Zelle'); // Mantenido por compatibilidad
        // ── Multipago ─────────────────────────────────────────────
        const tiposPago = ref([]);
        const bancosPorTipo = ref({}); // cache por tipoPagoId
        const pagos = ref([{ tipoPagoId: 0, monto: 0, bancoId: null, referencia: '', titular: '', fechaTransaccion: '', montoBs: null }]);
        const getTipoPago = (id) => tiposPago.value.find(t => t.tipoPagoId === id);
        const calcularVuelto = (p) => {
            const tipo = getTipoPago(p.tipoPagoId);
            if (!tipo || tipo.nombre !== 'Efectivo')
                return 0;
            const abonadoPorOtros = pagos.value.filter(x => x !== p).reduce((a, x) => a + (x.monto || 0), 0);
            const restante = totalCalculado.value - abonadoPorOtros;
            return Math.max(0, (p.monto || 0) - Math.max(0, restante));
        };
        const totalPagado = computed(() => pagos.value.reduce((acc, p) => acc + (p.monto || 0), 0));
        const vueltoTotal = computed(() => pagos.value.reduce((acc, p) => acc + calcularVuelto(p), 0));
        const pagoCompleto = computed(() => {
            if (totalCalculado.value <= 0)
                return false;
            const basePagada = totalPagado.value - vueltoTotal.value;
            const diff = Math.abs(basePagada - totalCalculado.value);
            const balanceOk = diff < 0.01;
            const infoOk = pagos.value.every(p => {
                if (p.tipoPagoId <= 0 || (p.monto || 0) <= 0)
                    return false;
                const tipo = getTipoPago(p.tipoPagoId);
                if (!tipo)
                    return false;
                // Validaciones dinámicas según los flags de la base de datos
                if (tipo.requiereReferencia && (!p.referencia || p.referencia.trim() === ''))
                    return false;
                if (tipo.requiereReferencia && !p.fechaTransaccion)
                    return false; // si pide ref, la fecha también es obligatoria en banco
                if (tipo.requiereTitular && (!p.titular || p.titular.trim() === ''))
                    return false;
                if (tipo.requiereBanco && (!p.bancoId || p.bancoId <= 0))
                    return false;
                return true;
            });
            return balanceOk && infoOk;
        });
        const getBancos = (tipoPagoId) => bancosPorTipo.value[tipoPagoId] || [];
        const onChangeTipoPago = async (p) => {
            const tipo = getTipoPago(p.tipoPagoId);
            if (tipo?.requiereBanco && !bancosPorTipo.value[p.tipoPagoId]) {
                const res = await PagoVentaService.getBancosPorTipoPago(p.tipoPagoId);
                if (res.exito)
                    bancosPorTipo.value[p.tipoPagoId] = res.datos;
            }
            // Resetear vuelto
            p.bancoId = null;
            p.referencia = '';
            p.titular = '';
            p.montoBs = null;
        };
        const agregarPago = () => {
            pagos.value.push({ tipoPagoId: 0, monto: 0, bancoId: null, referencia: '', titular: '', fechaTransaccion: '', montoBs: null });
        };
        const resetPagos = () => {
            pagos.value = [{ tipoPagoId: 0, monto: 0, bancoId: null, referencia: '', titular: '', fechaTransaccion: '', montoBs: null }];
        };
        // Estado del Modal de Motivo
        const mostrarModalMotivo = ref(false);
        const tipoModalMotivo = ref('parcial');
        const motivoSeleccionado = ref('');
        const procesandoModal = ref(false);
        const itemEnDevolucion = ref(null);
        const opcionesParcial = [
            { valor: 'Cambio', titulo: 'Cambio de Producto', descripcion: 'El cliente prefiere llevar otro repuesto.' },
            { valor: 'Error', titulo: 'Error del Vendedor', descripcion: 'Se despachó un repuesto incorrecto o que no aplica.' },
            { valor: 'Garantia', titulo: 'Garantía / Defecto', descripcion: 'El repuesto presenta un defecto de fábrica.' },
            { valor: 'Cancelado', titulo: 'Arrepentimiento', descripcion: 'El cliente ya no desea el artículo.' }
        ];
        const opcionesTotal = [
            { valor: 'ErrorFacturacion', titulo: 'Error en Facturación', descripcion: 'Datos incorrectos en el cobro, método pago errado, etc.' },
            { valor: 'DevolucionTotal', titulo: 'Devolución Total', descripcion: 'El cliente devuelve toda la mercancía de la factura.' },
            { valor: 'AnulacionAdmin', titulo: 'Anulación Administrativa', descripcion: 'Orden interna anulada por gerencia.' }
        ];
        const modalConfig = computed(() => {
            if (tipoModalMotivo.value === 'parcial') {
                return {
                    titulo: 'Devolución Parcial',
                    subtitulo: `NC para ${itemEnDevolucion.value?.nombreProducto}`,
                    descripcion: 'Seleccione el motivo de la devolución. Esto emitirá una Nota de Crédito por el monto del artículo.',
                    opciones: opcionesParcial
                };
            }
            else {
                return {
                    titulo: 'Anulación Total',
                    subtitulo: `Factura ${ordenSeleccionada.value?.codigoOrden}`,
                    descripcion: 'Seleccione por qué está anulando toda la factura. Se generará una NC por el monto total y los artículos regresarán al inventario después de que Almacén confirme.',
                    opciones: opcionesTotal
                };
            }
        });
        onMounted(() => {
            cargarCaja();
            cargarTiposPago();
        });
        const cargarTiposPago = async () => {
            const res = await PagoVentaService.getTiposPago();
            if (res.exito)
                tiposPago.value = res.datos;
        };
        watch(modoHistorial, () => { ordenSeleccionada.value = null; cargarCaja(); });
        const cargarCaja = async () => {
            try {
                loading.value = true;
                let res = modoHistorial.value ? await OrdenVentaService.getFacturadas() : await OrdenVentaService.getCaja();
                if (res.exito)
                    ordenes.value = res.datos;
            }
            catch (e) {
                console.error(e);
            }
            finally {
                loading.value = false;
            }
        };
        const toggleHistorial = () => { modoHistorial.value = !modoHistorial.value; };
        const ordenesVisibles = computed(() => {
            if (!filtroCodigo.value)
                return ordenes.value;
            const q = filtroCodigo.value.toLowerCase();
            return ordenes.value.filter(o => o.codigoOrden.toLowerCase().includes(q) || o.nombreCliente.toLowerCase().includes(q));
        });
        const seleccionarOrden = (orden) => {
            ordenSeleccionada.value = JSON.parse(JSON.stringify(orden));
            metodoPago.value = orden.metodoPago || 'Zelle';
            resetPagos();
        };
        const statusClass = (estado) => {
            switch (estado) {
                case 'Facturada': return 'pill-dark shadow-sm';
                case 'PendienteReingreso': return 'pill-warning pulse';
                case 'Anulada': return 'pill-danger';
                default: return 'pill-success';
            }
        };
        const statusLabel = (estado) => {
            switch (estado) {
                case 'Facturada': return 'PAGADA';
                case 'PendienteReingreso': return 'BLOQUEADO';
                case 'Anulada': return 'ANULADA';
                default: return 'DESPACHADA';
            }
        };
        const removerItem = (index) => {
            if (!ordenSeleccionada.value)
                return;
            const nombre = ordenSeleccionada.value.detalles[index].nombreProducto;
            Swal.fire({
                title: 'Remover Artículo',
                text: `¿El cliente se arrepintió de llevar ${nombre}? Esto bloqueará la caja hasta que Almacén reciba el ítem.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, solicitar reingreso',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#ef4444'
            }).then(async (result) => {
                if (result.isConfirmed && ordenSeleccionada.value) {
                    const copiaDetalles = JSON.parse(JSON.stringify(ordenSeleccionada.value.detalles));
                    copiaDetalles.splice(index, 1);
                    const dto = copiaDetalles.map((d) => ({ productoId: d.productoId, cantidad: d.cantidad, precioUnitarioUsd: d.precioUnitarioUsd }));
                    try {
                        Swal.showLoading();
                        const res = await OrdenVentaService.solicitarReingreso(ordenSeleccionada.value.ordenVentaId, dto);
                        if (res.exito) {
                            ordenSeleccionada.value = res.datos;
                            Swal.fire({ title: 'Reingreso Solicitado', text: 'Caja bloqueada. Entregue el repuesto al almacén.', icon: 'info', confirmButtonColor: '#3b82f6' });
                            await cargarCaja();
                        }
                    }
                    catch (e) {
                        Swal.fire('Error', 'No se pudo procesar', 'error');
                    }
                }
            });
        };
        const consultarEstado = async () => {
            if (!ordenSeleccionada.value)
                return;
            try {
                const res = await OrdenVentaService.getById(ordenSeleccionada.value.ordenVentaId);
                if (res.exito) {
                    const anteriorEstado = ordenSeleccionada.value.estado;
                    ordenSeleccionada.value = res.datos;
                    if (anteriorEstado === 'PendienteReingreso' && res.datos.estado === 'Despachada') {
                        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: '¡Almacén Confirmó!', showConfirmButton: false, timer: 3000 });
                        await cargarCaja();
                    }
                }
            }
            catch (e) { }
        };
        const totalCalculado = computed(() => {
            if (!ordenSeleccionada.value)
                return 0;
            return ordenSeleccionada.value.detalles.reduce((acc, current) => acc + (current.precioUnitarioUsd * current.cantidad), 0);
        });
        const facturar = async () => {
            if (!ordenSeleccionada.value)
                return;
            try {
                facturando.value = true;
                const res = await PagoVentaService.registrarPago({
                    ordenVentaId: ordenSeleccionada.value.ordenVentaId,
                    detalles: pagos.value.map(p => ({
                        tipoPagoId: p.tipoPagoId,
                        monedaId: 1,
                        bancoId: p.bancoId || undefined,
                        monto: p.monto,
                        referencia: p.referencia || undefined,
                        fechaTransaccion: p.fechaTransaccion || undefined,
                        titular: p.titular || undefined,
                        montoBs: p.montoBs || undefined,
                        vuelto: calcularVuelto(p) > 0 ? calcularVuelto(p) : undefined
                    }))
                });
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: '¡Factura Emitida!', text: `Total cobrado: $${totalCalculado.value.toFixed(2)}`, showConfirmButton: false, timer: 3500 });
                ordenSeleccionada.value = null;
                resetPagos();
                await cargarCaja();
            }
            catch (e) {
                Swal.fire('Error', e.message || 'No se pudo facturar', 'error');
            }
            finally {
                facturando.value = false;
            }
        };
        const abrirModalMotivo = (tipo, item) => {
            tipoModalMotivo.value = tipo;
            if (item)
                itemEnDevolucion.value = item;
            motivoSeleccionado.value = '';
            mostrarModalMotivo.value = true;
        };
        const cerrarModalMotivo = () => {
            mostrarModalMotivo.value = false;
            motivoSeleccionado.value = '';
            procesandoModal.value = false;
        };
        const confirmarMotivo = async () => {
            if (!ordenSeleccionada.value || !motivoSeleccionado.value)
                return;
            procesandoModal.value = true;
            if (tipoModalMotivo.value === 'parcial' && itemEnDevolucion.value) {
                try {
                    const item = itemEnDevolucion.value;
                    const dto = {
                        clienteId: ordenSeleccionada.value.clienteId,
                        ordenVentaId: ordenSeleccionada.value.ordenVentaId,
                        tipoDevolucion: 'Parcial (Cambio)',
                        motivo: motivoSeleccionado.value,
                        detalles: [{ productoId: item.productoId, cantidad: item.cantidad, precioUnitarioUsd: item.precioUnitarioUsd }]
                    };
                    const res = await NotaCreditoService.create(dto);
                    if (res.exito) {
                        Swal.fire({ title: 'Procesada', text: `NC ${res.datos.codigoNota} creada. Entregue al almacén.`, icon: 'success', confirmButtonColor: '#10b981' });
                        cerrarModalMotivo();
                    }
                }
                catch (e) {
                    Swal.fire('Error', 'No se pudo procesar la nota de crédito.', 'error');
                }
            }
            else if (tipoModalMotivo.value === 'total') {
                try {
                    const dto = {
                        clienteId: ordenSeleccionada.value.clienteId,
                        ordenVentaId: ordenSeleccionada.value.ordenVentaId,
                        tipoDevolucion: 'Anulación Total',
                        motivo: motivoSeleccionado.value,
                        detalles: ordenSeleccionada.value.detalles.map(d => ({ productoId: d.productoId, cantidad: d.cantidad, precioUnitarioUsd: d.precioUnitarioUsd }))
                    };
                    const res = await NotaCreditoService.create(dto);
                    if (res.exito) {
                        await OrdenVentaService.cambiarEstado(ordenSeleccionada.value.ordenVentaId, 'Anulada');
                        Swal.fire({ title: 'Anulada', text: `NC ${res.datos.codigoNota} emitida y factura anulada.`, icon: 'success' });
                        cerrarModalMotivo();
                        ordenSeleccionada.value = null;
                        await cargarCaja();
                    }
                }
                catch (e) {
                    Swal.fire('Error', 'No se pudo anular', 'error');
                }
            }
            procesandoModal.value = false;
        };
        return {
            ordenes, loading, ordenSeleccionada, metodoPago, statusClass, statusLabel, ListIcon, SearchIcon,
            seleccionarOrden, removerItem, totalCalculado, facturar, facturando, consultarEstado,
            modoHistorial, toggleHistorial, ordenesVisibles, filtroCodigo, anulando,
            mostrarModalMotivo, modalConfig, motivoSeleccionado, abrirModalMotivo, cerrarModalMotivo, confirmarMotivo, procesandoModal,
            // Multipago
            tiposPago, pagos, totalPagado, pagoCompleto, getTipoPago, getBancos,
            agregarPago, calcularVuelto, onChangeTipoPago, PlusCircleIcon
        };
    }
});
